import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, Crown, CreditCard, Calendar, Activity, Users } from 'lucide-react';
import { toast } from 'sonner';
import HeaderBar from '@/components/HeaderBar';
import AccountSidebar from '@/components/AccountSidebar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionPage({ token, user, logout }) {
    const navigate = useNavigate();
    
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        try {
            const response = await axios.get(`${API}/api/user/subscription`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubscription(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching subscription data:', error);
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            await axios.post(
                `${API}/api/subscription/cancel`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Subscription cancelled successfully');
            setShowCancelConfirm(false);
            // Refresh subscription data
            fetchSubscriptionData();
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
        } finally {
            setCancelLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const formatCurrency = (amount) => {
        if (!amount) return '---';
        return `$${amount.toFixed(2)}`;
    };

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (num >= 1000) {
            return (num / 1000).toFixed(0) + 'k';
        }
        return num.toString();
    };

    const getPlanDisplayName = () => {
        if (!subscription?.has_subscription) return 'Free Trial';
        const planType = subscription.plan_name || subscription.plan_type;
        const pageLimit = formatNumber(subscription.page_limit);
        const duration = subscription.plan_duration === 'yearly' ? '/year' : '/month';
        return `${planType} (${pageLimit} pageviews${duration})`;
    };

    return (
        <div className="settings-page">
            {/* Header Bar */}
            <HeaderBar user={user} logout={logout} />

            {/* Settings Header */}
            <div className="settings-header">
                <div className="settings-header-content settings-header-row">
                    <h1 className="settings-title">Subscription Settings</h1>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="back-button"
                    >
                        ← Back to dashboard
                    </Button>
                </div>
            </div>

            <div className="settings-container">
                <AccountSidebar token={token} />

                {/* Main Content */}
                <div className="settings-main">
                    <div className="settings-content">
                        {/* Subscription Hero Header */}
                        <div className="subscription-hero">
                            <div className="subscription-hero__icon">
                                <CreditCard className="w-5 h-5" />
                            </div>
                            <div className="subscription-hero__text">
                                <h2 className="subscription-hero__title">Subscription</h2>
                                <p className="subscription-hero__subtitle">Manage your plan, billing, and payment details.</p>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                            </div>
                        ) : (
                            <>
                                {/* Cancelled Subscription Notice */}
                                {!subscription?.has_subscription && subscription?.was_cancelled && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                            <div>
                                                <h3 className="font-medium text-yellow-800">Subscription Cancelled</h3>
                                                <p className="text-sm text-yellow-700 mt-1">
                                                    Your subscription has been cancelled. You&apos;re now on the free tier with limited features.
                                                </p>
                                                <Button 
                                                    className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white"
                                                    onClick={() => navigate('/choose-plan')}
                                                >
                                                    Resume Subscription
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Plan Summary Cards — softer feel inspired by illustrated card aesthetic */}
                                <div className="subscription-plan-grid">
                                    {/* Current Plan Card */}
                                    <div className="subscription-plan-card subscription-plan-card--primary">
                                        <div className="subscription-plan-card__icon">
                                            <Crown className="w-5 h-5" />
                                        </div>
                                        <div className="subscription-plan-card__label">Current Plan</div>
                                        <div className="subscription-plan-card__value">
                                            {getPlanDisplayName()}
                                        </div>
                                        {subscription?.has_subscription ? (
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                                                ● Active
                                            </span>
                                        ) : (
                                            <Link
                                                to="/choose-plan"
                                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                                            >
                                                Upgrade →
                                            </Link>
                                        )}
                                    </div>

                                    {/* Next Bill Amount Card */}
                                    <div className="subscription-plan-card subscription-plan-card--amount">
                                        <div className="subscription-plan-card__icon">
                                            <CreditCard className="w-5 h-5" />
                                        </div>
                                        <div className="subscription-plan-card__label">Next bill amount</div>
                                        <div className="subscription-plan-card__value">
                                            {subscription?.has_subscription
                                                ? formatCurrency(subscription.billing_amount)
                                                : '---'
                                            }
                                        </div>
                                        {subscription?.has_subscription && (
                                            <div className="subscription-plan-card__sublabel">
                                                {subscription.plan_duration === 'yearly' ? 'per year' : 'per month'}
                                            </div>
                                        )}
                                    </div>

                                    {/* Next Bill Date Card */}
                                    <div className="subscription-plan-card subscription-plan-card--date">
                                        <div className="subscription-plan-card__icon">
                                            <Calendar className="w-5 h-5" />
                                        </div>
                                        <div className="subscription-plan-card__label">Next bill date</div>
                                        <div className="subscription-plan-card__value">
                                            {subscription?.has_subscription
                                                ? formatDate(subscription.next_billing_date)
                                                : '---'
                                            }
                                        </div>
                                    </div>
                                </div>

                                {/* Usage Statistics */}
                                <div className="subscription-usage-section">
                                    <div className="subscription-usage-header">
                                        <div className="subscription-usage-header__icon">
                                            <Activity className="w-4 h-4" />
                                        </div>
                                        <h3 className="subscription-usage-header__title">Usage</h3>
                                        <span className="subscription-usage-header__badge">last 30 days</span>
                                    </div>

                                    <ul className="subscription-usage-list">
                                        <li className="subscription-usage-row">
                                            <span className="subscription-usage-row__label">Total billable pageviews</span>
                                            <span className="subscription-usage-row__value">
                                                {subscription?.usage?.billable_pageviews?.toLocaleString() || 0}
                                                <span className="subscription-usage-row__limit">
                                                    {' / '}{formatNumber(subscription?.page_limit || 10000)}
                                                </span>
                                            </span>
                                        </li>
                                        <li className="subscription-usage-row">
                                            <span className="subscription-usage-row__label">Pageviews</span>
                                            <span className="subscription-usage-row__value">
                                                {subscription?.usage?.pageviews?.toLocaleString() || 0}
                                            </span>
                                        </li>
                                        <li className="subscription-usage-row">
                                            <span className="subscription-usage-row__label">Custom events</span>
                                            <span className="subscription-usage-row__value">
                                                {subscription?.usage?.custom_events?.toLocaleString() || 0}
                                            </span>
                                        </li>
                                    </ul>

                                    {/* Progress bar for usage */}
                                    {subscription?.has_subscription && (() => {
                                        const pct = Math.min(100, Math.round((subscription.usage?.billable_pageviews || 0) / subscription.page_limit * 100));
                                        const barClass = pct >= 90 ? 'subscription-usage-bar--danger'
                                                       : pct >= 75 ? 'subscription-usage-bar--warn'
                                                       : '';
                                        return (
                                            <div className={`subscription-usage-bar ${barClass}`}>
                                                <div className="subscription-usage-bar__header">
                                                    <span className="subscription-usage-bar__label">Usage this period</span>
                                                    <span className="subscription-usage-bar__pct">{pct}%</span>
                                                </div>
                                                <div className="subscription-usage-bar__track">
                                                    <div
                                                        className="subscription-usage-bar__fill"
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Sites & Team Members Usage */}
                                <div className="subscription-usage-section">
                                    <div className="subscription-usage-header">
                                        <div className="subscription-usage-header__icon subscription-usage-header__icon--violet">
                                            <Users className="w-4 h-4" />
                                        </div>
                                        <h3 className="subscription-usage-header__title">Sites &amp; team members</h3>
                                    </div>

                                    <ul className="subscription-usage-list">
                                        <li className="subscription-usage-row">
                                            <span className="subscription-usage-row__label">Owned sites</span>
                                            <span className="subscription-usage-row__value">
                                                {subscription?.usage?.websites || 0}
                                                <span className="subscription-usage-row__limit">
                                                    {' / '}{subscription?.website_limit || 1}
                                                </span>
                                            </span>
                                        </li>
                                        <li className="subscription-usage-row">
                                            <span className="subscription-usage-row__label">Team members</span>
                                            <span className="subscription-usage-row__value">
                                                {subscription?.usage?.team_members || 0}
                                                <span className="subscription-usage-row__limit">
                                                    {' / '}{subscription?.plan_type === 'growth' ? 3 : subscription?.plan_type === 'business' ? 10 : 1}
                                                </span>
                                            </span>
                                        </li>
                                    </ul>
                                </div>

                                {/* Action Buttons */}
                                <div className="subscription-actions">
                                    {subscription?.has_subscription ? (
                                        <div className="flex flex-col gap-4">
                                            <div className="subscription-actions__buttons">
                                                <Button
                                                    variant="outline"
                                                    className="px-6 py-2.5 text-sm font-medium"
                                                    onClick={() => navigate('/choose-plan')}
                                                >
                                                    Change Plan
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="px-6 py-2.5 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                                    onClick={() => setShowCancelConfirm(true)}
                                                >
                                                    Cancel Subscription
                                                </Button>
                                            </div>

                                            {/* Cancel Confirmation Dialog */}
                                            {showCancelConfirm && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                                                    <div className="flex items-start gap-3">
                                                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                                                        <div className="flex-1">
                                                            <h3 className="font-medium text-red-800">Cancel Subscription?</h3>
                                                            <p className="text-sm text-red-700 mt-1">
                                                                Are you sure you want to cancel your subscription? You will lose access to premium features and your plan will be downgraded to the free tier.
                                                            </p>
                                                            <div className="flex gap-3 mt-4">
                                                                <Button 
                                                                    variant="destructive"
                                                                    className="bg-red-600 hover:bg-red-700"
                                                                    onClick={handleCancelSubscription}
                                                                    disabled={cancelLoading}
                                                                >
                                                                    {cancelLoading ? (
                                                                        <>
                                                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                                            Cancelling...
                                                                        </>
                                                                    ) : (
                                                                        'Yes, Cancel Subscription'
                                                                    )}
                                                                </Button>
                                                                <Button 
                                                                    variant="outline"
                                                                    onClick={() => setShowCancelConfirm(false)}
                                                                    disabled={cancelLoading}
                                                                >
                                                                    Keep Subscription
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <Button 
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 text-base font-medium"
                                            onClick={() => navigate('/choose-plan')}
                                        >
                                            {subscription?.was_cancelled ? 'Resume Subscription' : 'Upgrade'}
                                        </Button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
